import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

function groupPermissionsByModule(permissions) {
  const g = {};
  (permissions || []).forEach((p) => {
    const m = p.module || 'other';
    if (!g[m]) g[m] = [];
    g[m].push(p);
  });
  Object.keys(g).forEach((k) => {
    g[k].sort((a, b) => String(a.code).localeCompare(String(b.code)));
  });
  return g;
}

export default function AdminSeedDataRbacWorkspace({ onRefreshCatalog }) {
  const { hasPermission } = useAuth();
  const canEditRoles = hasPermission('roles_modify');
  const canEditGroups = hasPermission('groups_modify');

  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [innerTab, setInnerTab] = useState(0);
  const [permFilter, setPermFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const [roleDialog, setRoleDialog] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', is_active: true, permission_ids: [] });
  const [roleSaving, setRoleSaving] = useState(false);

  const [groupDialog, setGroupDialog] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '', is_active: true, role_ids: [] });
  const [groupSaving, setGroupSaving] = useState(false);

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/seed-data/rbac/workspace');
      setWorkspace(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load RBAC workspace');
      setWorkspace(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const grouped = useMemo(() => groupPermissionsByModule(workspace?.permissions), [workspace?.permissions]);

  const filteredGrouped = useMemo(() => {
    const q = permFilter.trim().toLowerCase();
    if (!q) return grouped;
    const out = {};
    Object.entries(grouped).forEach(([mod, perms]) => {
      const hit = perms.filter(
        (p) =>
          String(p.code || '').toLowerCase().includes(q) ||
          String(p.name || '').toLowerCase().includes(q) ||
          String(p.module || '').toLowerCase().includes(q)
      );
      if (hit.length) out[mod] = hit;
    });
    return out;
  }, [grouped, permFilter]);

  const openRoleEdit = (role) => {
    setRoleForm({
      name: role.name,
      description: role.description || '',
      is_active: !!role.is_active,
      permission_ids: (role.permissions || []).map((p) => p.id)
    });
    setRoleDialog(role);
  };

  const togglePerm = (id) => {
    setRoleForm((prev) => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(id)
        ? prev.permission_ids.filter((x) => x !== id)
        : [...prev.permission_ids, id]
    }));
  };

  const toggleModulePerms = (moduleName) => {
    const ids = (grouped[moduleName] || []).map((p) => p.id);
    if (!ids.length) return;
    setRoleForm((prev) => {
      const allOn = ids.every((id) => prev.permission_ids.includes(id));
      return {
        ...prev,
        permission_ids: allOn
          ? prev.permission_ids.filter((id) => !ids.includes(id))
          : [...new Set([...prev.permission_ids, ...ids])]
      };
    });
  };

  const saveRole = async () => {
    if (!roleDialog) return;
    setRoleSaving(true);
    try {
      await api.put(`/roles/${roleDialog.id}`, roleForm);
      toast.success('Role updated');
      setRoleDialog(null);
      await loadWorkspace();
      if (onRefreshCatalog) await onRefreshCatalog();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save role');
    } finally {
      setRoleSaving(false);
    }
  };

  const openGroupEdit = (group) => {
    setGroupForm({
      name: group.name,
      description: group.description || '',
      is_active: !!group.is_active,
      role_ids: (group.roles || []).map((r) => r.id)
    });
    setGroupDialog(group);
  };

  const toggleRoleInGroup = (roleId) => {
    setGroupForm((prev) => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((x) => x !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  const saveGroup = async () => {
    if (!groupDialog) return;
    setGroupSaving(true);
    try {
      await api.put(`/groups/${groupDialog.id}`, groupForm);
      toast.success('Group updated');
      setGroupDialog(null);
      await loadWorkspace();
      if (onRefreshCatalog) await onRefreshCatalog();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save group');
    } finally {
      setGroupSaving(false);
    }
  };

  const reapplyBaseline = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/seed-data/rbac/re-apply');
      toast.success(r.data?.message || 'RBAC baseline re-applied');
      const next = r.data?.data?.workspace;
      if (next) setWorkspace(next);
      else await loadWorkspace();
      if (onRefreshCatalog) await onRefreshCatalog();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Re-apply failed');
    } finally {
      setBusy(false);
    }
  };

  const runRbacSeed = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/seed-data/rbac_baseline/run');
      const msg = r.data?.message || 'Done';
      if (r.data?.data?.skipped) toast.info(msg);
      else toast.success(msg);
      await loadWorkspace();
      if (onRefreshCatalog) await onRefreshCatalog();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Seed failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !workspace) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const st = workspace?.rbacStatus;
  const manifest = workspace?.constantsManifest;

  return (
    <Stack spacing={2}>
      <Alert severity="info" icon={<SecurityIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          RBAC workspace
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {workspace?.note}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Standard seed card <strong>RBAC baseline</strong> lives under the <strong>Access &amp; identity</strong> tab. The
          regular <strong>Seed</strong> button skips when baseline looks complete; use <strong>Re-apply baseline</strong>{' '}
          below to sync permission rows from code (for example after adding modules in <code>seedConstants</code>) without
          resetting your role customizations.
        </Typography>
      </Alert>

      {st ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={st.complete ? 'Baseline complete' : 'Baseline incomplete'}
            color={st.complete ? 'success' : 'warning'}
          />
          <Typography variant="body2" color="text.secondary">
            {st.summary}
          </Typography>
        </Stack>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadWorkspace} disabled={busy}>
          Refresh
        </Button>
        <Tooltip title="Runs the same executor as RBAC baseline even when status is already “complete” (merges new permissions from code).">
          <span>
            <Button
              size="small"
              variant="contained"
              onClick={reapplyBaseline}
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Re-apply baseline
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Uses the normal seed endpoint; may no-op if baseline is already satisfied.">
          <span>
            <Button size="small" variant="outlined" onClick={runRbacSeed} disabled={busy}>
              Run RBAC seed
            </Button>
          </span>
        </Tooltip>
        <Link component={RouterLink} to="/admin/roles" variant="body2" sx={{ alignSelf: 'center', ml: 1 }}>
          App Roles admin
        </Link>
        <Link component={RouterLink} to="/admin/groups" variant="body2" sx={{ alignSelf: 'center' }}>
          Groups admin
        </Link>
      </Stack>

      <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Permissions" />
        <Tab label="Roles" />
        <Tab label="Groups" />
        <Tab label="Definitions (code)" />
      </Tabs>

      {innerTab === 0 ? (
        <Box>
          <TextField
            size="small"
            fullWidth
            placeholder="Filter permissions by code, name, or module…"
            value={permFilter}
            onChange={(e) => setPermFilter(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Rows are stored in the database; canonical codes are generated from backend seed constants. Editing here is not
            supported — adjust mappings in code and re-apply baseline.
          </Typography>
          {Object.keys(filteredGrouped).length === 0 ? (
            <Typography color="text.secondary">No permissions match the filter.</Typography>
          ) : (
            Object.keys(filteredGrouped)
              .sort()
              .map((mod) => (
                <Accordion key={mod} defaultExpanded={Object.keys(filteredGrouped).length <= 6}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {mod}{' '}
                      <Chip component="span" size="small" label={filteredGrouped[mod].length} sx={{ ml: 1 }} />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={0.5}>
                      {filteredGrouped[mod].map((p) => (
                        <Box
                          key={p.id}
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignItems: 'baseline',
                            py: 0.25,
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {p.code}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {p.name}
                          </Typography>
                          {p.description ? (
                            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                              {p.description}
                            </Typography>
                          ) : null}
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))
          )}
        </Box>
      ) : null}

      {innerTab === 1 ? (
        <Grid container spacing={2}>
          {(workspace?.roles || []).map((role) => (
            <Grid item xs={12} md={6} key={role.id}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {role.name}
                      </Typography>
                      {role.description ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {role.description}
                        </Typography>
                      ) : null}
                    </Box>
                    <Chip size="small" label={role.is_active ? 'Active' : 'Inactive'} color={role.is_active ? 'success' : 'default'} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {(role.permissions || []).length} permission(s)
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                    {(role.permissions || []).slice(0, 12).map((p) => (
                      <Chip key={p.id} size="small" variant="outlined" label={p.code} />
                    ))}
                    {(role.permissions || []).length > 12 ? (
                      <Chip size="small" label={`+${(role.permissions || []).length - 12} more`} />
                    ) : null}
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    sx={{ mt: 2 }}
                    disabled={!canEditRoles}
                    onClick={() => openRoleEdit(role)}
                  >
                    Edit permissions
                  </Button>
                  {!canEditRoles ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Requires <code>roles_modify</code>.
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : null}

      {innerTab === 2 ? (
        <Grid container spacing={2}>
          {(workspace?.groups || []).map((group) => (
            <Grid item xs={12} md={6} key={group.id}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {group.name}
                      </Typography>
                      {group.description ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {group.description}
                        </Typography>
                      ) : null}
                    </Box>
                    <Chip size="small" label={group.is_active ? 'Active' : 'Inactive'} color={group.is_active ? 'success' : 'default'} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Roles
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {(group.roles || []).map((r) => (
                      <Chip key={r.id} size="small" variant="outlined" label={r.name} />
                    ))}
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    sx={{ mt: 2 }}
                    disabled={!canEditGroups}
                    onClick={() => openGroupEdit(group)}
                  >
                    Edit roles
                  </Button>
                  {!canEditGroups ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Requires <code>groups_modify</code>.
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : null}

      {innerTab === 3 ? (
        manifest ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Expected permission count: <strong>{manifest.expectedPermissionCount}</strong> · Modules:{' '}
              <strong>{manifest.modules?.length ?? 0}</strong> · Actions: <strong>{manifest.actions?.length ?? 0}</strong> ·
              Special: <strong>{manifest.specialPermissions?.length ?? 0}</strong>
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                overflow: 'auto',
                fontSize: 11,
                maxHeight: 480
              }}
            >
              {JSON.stringify(manifest, null, 2)}
            </Box>
          </Box>
        ) : (
          <Typography color="text.secondary">No manifest loaded.</Typography>
        )
      ) : null}

      <Dialog open={!!roleDialog} onClose={() => !roleSaving && setRoleDialog(null)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Edit role: {roleDialog?.name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={roleForm.name}
              onChange={(e) => setRoleForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={roleForm.description}
              onChange={(e) => setRoleForm((p) => ({ ...p, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={roleForm.is_active}
                  onChange={(e) => setRoleForm((p) => ({ ...p, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
            <Typography variant="subtitle2">Permissions</Typography>
            {Object.keys(grouped)
              .sort()
              .map((mod) => (
                <Accordion key={`dlg-${mod}`} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', pr: 1 }}>
                      <Typography sx={{ flex: 1, fontWeight: 600 }}>{mod}</Typography>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleModulePerms(mod);
                        }}
                      >
                        Toggle module
                      </Button>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormGroup>
                      {(grouped[mod] || []).map((p) => (
                        <FormControlLabel
                          key={p.id}
                          control={
                            <Checkbox
                              checked={roleForm.permission_ids.includes(p.id)}
                              onChange={() => togglePerm(p.id)}
                            />
                          }
                          label={
                            <span>
                              <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {p.code}
                              </Typography>{' '}
                              <Typography component="span" variant="body2" color="text.secondary">
                                — {p.name}
                              </Typography>
                            </span>
                          }
                        />
                      ))}
                    </FormGroup>
                  </AccordionDetails>
                </Accordion>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog(null)} disabled={roleSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveRole} disabled={roleSaving}>
            {roleSaving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!groupDialog} onClose={() => !groupSaving && setGroupDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit group: {groupDialog?.name}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={groupForm.name}
              onChange={(e) => setGroupForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={groupForm.description}
              onChange={(e) => setGroupForm((p) => ({ ...p, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={groupForm.is_active}
                  onChange={(e) => setGroupForm((p) => ({ ...p, is_active: e.target.checked }))}
                />
              }
              label="Active"
            />
            <Typography variant="subtitle2">Roles in this group</Typography>
            <FormGroup>
              {(workspace?.roles || []).map((r) => (
                <FormControlLabel
                  key={r.id}
                  control={
                    <Checkbox checked={groupForm.role_ids.includes(r.id)} onChange={() => toggleRoleInGroup(r.id)} />
                  }
                  label={r.name}
                />
              ))}
            </FormGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialog(null)} disabled={groupSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveGroup} disabled={groupSaving}>
            {groupSaving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
